import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  doc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Review } from '../../types/review';

const COLLECTION = 'reviews';
const BIDS_COLLECTION = 'bids';

const toIso = (val: unknown): string => {
  if (!val) return new Date().toISOString();
  if (val instanceof Timestamp) return val.toDate().toISOString();
  return String(val);
};

const docToReview = (id: string, data: Record<string, unknown>): Review => ({
  id,
  reviewedCompanyId: data.reviewedCompanyId as string,
  reviewedCompanyName: data.reviewedCompanyName as string,
  reviewedByCompanyId: data.reviewedByCompanyId as string,
  reviewedByCompanyName: data.reviewedByCompanyName as string,
  reviewedByUserId: data.reviewedByUserId as string,
  offerId: data.offerId as string,
  offerRoute: data.offerRoute as string,
  rating: data.rating as number,
  comment: data.comment as string | undefined,
  createdAt: toIso(data.createdAt),
});

export class ReviewService {
  /**
   * Create a new review. The doc id is `offerId_reviewerCompanyId` — the rules
   * require exactly this id (SEC-7), which makes reviews write-once per deal
   * per reviewing company (update/delete are locked). The company rating
   * aggregate is recomputed server-side by the `onReviewCreated` trigger —
   * `companies.rating/reviewCount` are not client-writable.
   */
  async createReview(data: Omit<Review, 'id' | 'createdAt'>): Promise<Review> {
    const reviewId = `${data.offerId}_${data.reviewedByCompanyId}`;
    await setDoc(doc(db, COLLECTION, reviewId), {
      ...data,
      createdAt: serverTimestamp(),
    });

    return {
      ...data,
      id: reviewId,
      createdAt: new Date().toISOString(),
    };
  }

  /** Fetch reviews for a company ordered by newest first */
  async getReviewsForCompany(
    companyId: string,
    limitCount: number = 50
  ): Promise<Review[]> {
    const q = query(
      collection(db, COLLECTION),
      where('reviewedCompanyId', '==', companyId),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) =>
      docToReview(d.id, d.data() as Record<string, unknown>)
    );
  }

  /** Check whether a company has already reviewed a specific offer */
  async hasReviewed(fromCompanyId: string, offerId: string): Promise<boolean> {
    const q = query(
      collection(db, COLLECTION),
      where('reviewedByCompanyId', '==', fromCompanyId),
      where('offerId', '==', offerId)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  /**
   * Return completed transports involving both companies with route info.
   * Checks two directions:
   *   1. myCompany was carrier, theirCompany was shipper
   *   2. myCompany was shipper, theirCompany was carrier
   */
  async getCompletedOffersBetween(
    myCompanyId: string,
    theirCompanyId: string
  ): Promise<{ offerId: string; offerRoute: string }[]> {
    const seen = new Set<string>();
    const results: { offerId: string; offerRoute: string }[] = [];

    const collect = (data: Record<string, unknown>) => {
      const offerId = data.offerId as string;
      const offerRoute = (data.offerRoute as string) || offerId;
      if (offerId && !seen.has(offerId)) {
        seen.add(offerId);
        results.push({ offerId, offerRoute });
      }
    };

    // Direction 1: I was the carrier
    const asCarrierSnap = await getDocs(
      query(
        collection(db, BIDS_COLLECTION),
        where('bidderCompanyId', '==', myCompanyId),
        where('ownerCompanyId', '==', theirCompanyId),
        where('status', '==', 'accepted')
      )
    );
    asCarrierSnap.docs.forEach((d) => collect(d.data() as Record<string, unknown>));

    // Direction 2: I was the shipper (they were the carrier)
    const asShipperSnap = await getDocs(
      query(
        collection(db, BIDS_COLLECTION),
        where('ownerCompanyId', '==', myCompanyId),
        where('bidderCompanyId', '==', theirCompanyId),
        where('status', '==', 'accepted')
      )
    );
    asShipperSnap.docs.forEach((d) => collect(d.data() as Record<string, unknown>));

    return results;
  }
}

export const reviewService = new ReviewService();
